<template>
  <div class="min-h-screen bg-gray-100 flex relative main-content"> 
    <main class="flex-1 p-6 overflow-y-auto page-container">
      <h1 class="text-2xl font-semibold mb-4">{{ $t('components.adminDashboard.allDomains') }}</h1>
      <div class="bg-white p-4 rounded shadow">
        <div class="mb-4 flex items-center justify-between">
          <el-input
            v-model="search"
            @input="onSearchChange"
            type="text"
            :placeholder="$t('components.adminDashboard.searchByDomainName')"
            class="search w-1/3"
            size="large"
          />
          <router-link to="/admin/domains/create">
            <el-button type="success" size="large" class="ml-auto">
              {{ $t('components.adminDashboard.addDomain') }}
              <el-icon style="margin-left: 5px;">
                <Plus />
              </el-icon>
            </el-button>
          </router-link>
        </div>

        <div class="overflow-auto max-h-table mb-4">
          <el-table v-loading="isFetching" :data="domains ?? []" style="width: 100%">
            <el-table-column type="index" :label="'No'" width="100" :index="(idx:number) => (currentPage - 1) * itemsPerPage + idx + 1" />
            <el-table-column :label="$t('components.table.logo')">
              <template #default="{ row }">
                <img :src="row.logo" alt="Logo" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('components.table.urlSuffix')">
              <template #default="{ row }">
                <p class="text-lg">{{ row.urlSuffix }}</p>
              </template>
            </el-table-column>
            <el-table-column :label="$t('components.table.isReleased')">
              <template #default="{ row }">
                <el-icon :size="25" v-if="row.released" color="green">
                  <Check />
                </el-icon>
                <el-icon :size="25" v-else color="red">
                  <Close />
                </el-icon>
              </template>
            </el-table-column>
            <el-table-column :label="$t('components.table.actions')" width="160" fixed="right">
              <template #default="{ row }">
                <router-link :to="{ name: 'AdminDomainsEdit', params: { id: row.domainId } }">
                  <el-button type="primary" size="large">
                    {{ $t('components.adminDashboard.editDomain.page.title') }}
                    <el-icon style="margin-left: 6px;">
                      <Edit />
                    </el-icon>
                  </el-button>
                </router-link>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="flex justify-between items-center gap-2">
          <div>
            <el-button @click="prevPage" :disabled="!hasPrev || isFetchingData" class="border rounded hover:bg-gray-100 disabled:opacity-50">Previous</el-button>
            <el-button @click="nextPage" :disabled="!hasNext || isFetchingData" class="border rounded hover:bg-gray-100 disabled:opacity-50">Next</el-button>
          </div>
          <span class="px-4 py-3">Page {{ currentPage }} of {{ totalPages }}</span>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import '../styles/table.scss';
import { getDomainsWithPaging } from '@/services/api/domainService';
import { DomainsWithPaging } from '@/types/api/Raw/domain';
import { defaultFirstCursor } from '@/types/constants/pagination';
import { Check, Close } from '@element-plus/icons-vue'

defineOptions({
  name: 'DomainOverviewPage'
});

const itemsPerPage = 5;
const domanResponse = ref<DomainsWithPaging | null>(null);
const search = ref('');
const isFetching = ref(false);
const hasNext = ref(false);
const hasPrev = ref(false);
const totalPages = ref(1);
const currentPage = ref(1);
let searchTimeout = 0;
const isFetchingData = ref(false);

const domains = computed(() => {
  return domanResponse.value?.edges.map(edge => edge.node);
});

const onSearchChange = (value: string) => {
  clearTimeout(searchTimeout);
  searchTimeout = window.setTimeout(() => {
    fetchData({
      search: value,
      first: itemsPerPage,
      after: defaultFirstCursor
    });
  }, 1000);
  currentPage.value = 1;
};

const nextPage = async () => {
  if (domanResponse.value?.pageInfo.endCursor) {
    isFetchingData.value = true;
    try {
      await fetchData({
        first: itemsPerPage,
        after: domanResponse.value?.pageInfo.endCursor
      });
      currentPage.value++;
    } catch (error) {
      console.error('Failed to fetch next page:', error);
    } finally {
      isFetchingData.value = false;
    }
  }
};

const prevPage = async () => {
  if (!domanResponse.value?.pageInfo.startCursor) return;
  isFetchingData.value = true;
  try {
    await fetchData({
      last: itemsPerPage,
      before: domanResponse.value?.pageInfo.startCursor
    });
    currentPage.value--;
  } catch (error) {
    console.error('Failed to fetch previous page:', error);
  } finally {
    isFetchingData.value = false;
  }
};

const fetchData = async ({
  search,
  after,
  before,
  first,
  last
}: {
  search?: string;
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}) => {
  isFetching.value = true;
  const domainsPagingRes = await getDomainsWithPaging({
    first,
    last,
    after,
    before,
    search
  });
  domanResponse.value = domainsPagingRes;
  totalPages.value = Math.ceil((domainsPagingRes.totalCount ?? 1) / itemsPerPage);
  hasNext.value = domainsPagingRes?.pageInfo?.hasNextPage ?? false;
  hasPrev.value = domainsPagingRes?.pageInfo?.hasPreviousPage ?? false;
  isFetching.value = false;
};

onMounted(() => {
  console.log('OverviewPage: onMounted');
  fetchData({
    first: itemsPerPage,
    after: defaultFirstCursor
  });
});
</script>

<style lang="scss" scoped>
.logo {
  width: 100px;
}
</style>
